import React, { useState, type FC, type PropsWithChildren, useContext, type KeyboardEvent, useEffect } from 'react';

import kebabCase from 'lodash/kebabCase';
import { withImage, useSmartlingStrings } from '@lucid/hooks';
import { getColor } from '@lucid/theme';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Text from '../text/text';
import Link from '../link/link';
import Heading from '../heading/heading';
import Container from '../container/container';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import LayoutContext from '../context/layout-context';
import Tab from '../tab/tab';

export interface TabCarouselProps {
  /** The Heading above the carousel */
  heading?: string;
  /** The font color of the heading */
  headingColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The accompanying text for the heading section */
  text?: string;
  /** Allows the component to get additional styles. */
  items: Lucid.Directus.Generated.Carousel_Item[];
  /** Determines whether the Item links should be displayed as buttons */
  shouldDisplayItemLinksAsButtons?: boolean;
  /** The CTA for the Image Carousel section
   ** Will override individual Item CTAs
   */
  link?: Lucid.Directus.Generated.Links;
  /** Will fill image container in carousel */
  shouldFillImages?: boolean;
}

const TabCarousel: FC<PropsWithChildren<TabCarouselProps>> = ({
  items,
  heading,
  headingColor,
  text,
  link,
  shouldFillImages = false,
  shouldDisplayItemLinksAsButtons = false,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_template_aria_label_slide_title'],
    shortCode,
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentItem, setCurrentItem] = useState(items[0]);

  const handleUpdateCarousel = (index: number) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    setCurrentItem(items[activeIndex]);
  }, [items, activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Tab') return;
    const currentEl = e.target as HTMLElement;
    e.preventDefault();
    let idx = 0;

    switch (e.key) {
      case 'ArrowRight':
        idx = (activeIndex + 1) % items.length;
        setActiveIndex(idx);
        (currentEl.parentNode?.children[idx] as HTMLElement).focus();
        break;
      case 'ArrowLeft':
        idx = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
        setActiveIndex(idx);
        (currentEl.parentNode?.children[idx] as HTMLElement).focus();
        break;
      case 'Enter':
        setActiveIndex(index);
        break;
      case 'Space':
        setActiveIndex(index);
        break;
    }
  };

  return (
    <Box css={{ label: 'tabCarousel', overflow: 'hidden' }}>
      <Container css={theme => ({ padding: `${theme.space.s104}px ${theme.space.s24}px` })}>
        <Box
          css={{
            maxWidth: '680px',
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          <Heading
            as="h2"
            css={theme => ({
              color:
                getColor({
                  color: headingColor,
                  theme,
                }) ?? theme.colors.primary,
              marginBottom: theme.space.s24,
            })}
            id="tabCarouselHeader"
            variant="heading2XL"
          >
            {heading}
          </Heading>
          <Text css={{ margin: 0 }} variant="paragraphM400">
            {text}
          </Text>
        </Box>
        <Box
          css={theme => ({
            paddingTop: theme.space.s64,
            paddingBottom: theme.space.s40,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: theme.space.s24,
          })}
          role="tablist"
        >
          {items?.map((card, index) => {
            const isActiveCard = index === activeIndex;

            return (
              <Tab
                key={`${card.id}-tablist-link`}
                aria-controls={`cardCarouselItem-${index}`}
                aria-label={withSmartling('mktg_template_aria_label_slide_title', { title: card.heading || '' })}
                aria-selected={card === currentItem}
                id={`cardCarouselTitle-${card.id}`}
                isActive={isActiveCard}
                label={card.heading}
                onClick={() => handleUpdateCarousel(index)}
                onKeyDown={e => handleKeyDown(e, index)}
                role="tab"
                tabIndex={isActiveCard ? 0 : -1}
                variant="secondary"
              />
            );
          })}
        </Box>
        <Box
          aria-live="polite"
          aria-roledescription="slide"
          css={theme => ({
            display: 'grid',
            maxWidth: '980px',
            margin: 'auto',
            gridTemplateColumns: `repeat(${items.length}, minmax(100%, 980px))`,
            gap: theme.space.s40,
          })}
          id={`${kebabCase(currentItem.heading)}-tab`}
          role="tabpanel"
        >
          {items.map((card, index) => {
            const isActiveCard = index === activeIndex;

            return (
              <Box
                key={`${card.id}-tablist-image-container`}
                aria-hidden={!isActiveCard}
                aria-label={currentItem?.text}
                css={theme => ({
                  transform: `translateX(calc(-${activeIndex * 100}% - ${40 * activeIndex}px))`,
                  transition: `transform 500ms ${theme.beziers.slowStartEase}`,
                  display: 'flex',
                  justifyContent: 'center',
                  borderRadius: theme.radii.r16,
                  maxWidth: '980px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                })}
                onClick={() => handleUpdateCarousel(index)}
                onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => handleKeyDown(e, index)}
                role="group"
                tabIndex={isActiveCard ? 0 : -1}
              >
                <ImageWithPlaceholder
                  alt={card.media?.translations?.[0]?.alt || ''}
                  aspectRatio="16:9"
                  css={{ maxHeight: '550px' }}
                  isBackgroundImage={shouldFillImages}
                  src={withImage(card.media)}
                />
              </Box>
            );
          })}
          {currentItem?.text && (
            <Box key={currentItem.id}>
              <Text id={`tabCarouselDescription-${currentItem.id}`} variant="paragraphL400">
                {currentItem?.text}
              </Text>
              {currentItem?.link && currentItem?.link?.final_url && (
                <Link
                  aria-describedby={`tabCarouselDescription-${currentItem.id}`}
                  asButton={shouldDisplayItemLinksAsButtons}
                  css={{ fontSize: '20px' }}
                  href={currentItem?.link.final_url}
                  iconAnimation="navigational"
                  type={shouldDisplayItemLinksAsButtons ? 'button' : undefined}
                  variant="primary"
                >
                  {currentItem?.link.default_link_text}
                </Link>
              )}
            </Box>
          )}
        </Box>
        {link?.final_url && (
          <Box
            css={theme => ({
              display: 'flex',
              justifyContent: 'center',
              marginTop: `${theme.space.s64}px`,
            })}
          >
            <Link asButton aria-describedby="tabCarouselHeader" href={link.final_url} type="button">
              {link?.default_link_text}
            </Link>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export { TabCarousel };
export default TabCarousel;
